// ============================================================
//  src/admin/components/AdminLayout.js
// ============================================================
import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ConfirmProvider } from './ConfirmModal';
import '../styles/admin.css';

const NAV_ITEMS = [
  { to: '/bot-father/dashboard',  label: 'Dashboard',  icon: '📊' },
  { to: '/bot-father/users',      label: 'Users',      icon: '👤' },
  { to: '/bot-father/bots',       label: 'Bots',       icon: '🤖' },
  { to: '/bot-father/broadcasts', label: 'Broadcasts', icon: '📣' },
  { to: '/bot-father/plans',      label: 'Plans',      icon: '💳' },
  { to: '/bot-father/payments',   label: 'Payments',   icon: '💰' },
  { to: '/bot-father/pocketfi',   label: 'PocketFi',   icon: '⚙️' },
  { to: '/bot-father/rent-space', label: 'Rent Space', icon: '🏠' },
  { to: '/bot-father/widgets',    label: 'Widgets',    icon: '💬' },
  { to: '/bot-father/contact-saver', label: 'Contact Saver', icon: '📇' },
  { to: '/bot-father/logs',       label: 'Logs',       icon: '📜' },
];

// Only visible to admins with aRole === 'superadmin' — the backend enforces
// this too (authSuperAdmin()), this just keeps the nav from showing an item
// that would 403 for everyone else.
const SUPERADMIN_NAV_ITEMS = [
  { to: '/bot-father/admins', label: 'Admins', icon: '🔐' },
];

function initials(name) {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || 'A';
}

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = navOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [navOpen]);

  const currentLabel = [...NAV_ITEMS, ...SUPERADMIN_NAV_ITEMS].find(i => location.pathname.startsWith(i.to))?.label || 'Bot-Father';

  async function handleLogout() {
    await logout();
    toast.success('Logged out');
    navigate('/bot-father/login', { replace: true });
  }

  return (
    <div className="bf-admin">
      <ConfirmProvider>
        <div className="bf-shell">

          {/* Overlay (mobile only) */}
          <div className={`bf-overlay${navOpen ? ' bf-open' : ''}`} onClick={() => setNavOpen(false)} />

          {/* Sidebar */}
          <aside className={`bf-sidebar${navOpen ? ' bf-open' : ''}`}>
            <div className="bf-sidebar-brand">
              <div className="bf-sidebar-brand-icon">🛡️</div>
              <div>
                <div className="bf-sidebar-brand-name">Bot-Father</div>
                <div className="bf-sidebar-brand-sub">Admin Panel</div>
              </div>
              <button className="bf-sidebar-close" aria-label="Close menu" onClick={() => setNavOpen(false)}>✕</button>
            </div>

            <nav className="bf-nav">
              {NAV_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `bf-nav-link${isActive ? ' bf-active' : ''}`}
                >
                  <span className="bf-nav-icon">{item.icon}</span> {item.label}
                </NavLink>
              ))}
              {admin?.aRole === 'superadmin' && SUPERADMIN_NAV_ITEMS.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `bf-nav-link${isActive ? ' bf-active' : ''}`}
                >
                  <span className="bf-nav-icon">{item.icon}</span> {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="bf-sidebar-footer">
              <div className="bf-admin-identity">
                <div className="bf-avatar">{initials(admin?.aName)}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="bf-admin-name">{admin?.aName}</div>
                  <div className="bf-admin-email">{admin?.aEmail}</div>
                </div>
              </div>
              <button className="btn btn-outline bf-full" style={{ fontSize: 12.5 }} onClick={handleLogout}>
                Log out
              </button>
            </div>
          </aside>

          {/* Content column: mobile topbar stacked above the main content */}
          <div className="bf-content-col">
            <div className="bf-topbar">
              <button className="bf-hamburger" aria-label="Open menu" onClick={() => setNavOpen(true)}>☰</button>
              <div className="bf-topbar-brand">
                <div className="bf-topbar-title">{currentLabel}</div>
              </div>
            </div>

            <main className="bf-main">
              <Outlet />
            </main>
          </div>
        </div>
      </ConfirmProvider>
    </div>
  );
}